import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";
import authConfig from "../authConfig";

export const API_BASE_URL = getApiBaseUrl();

// Refresh the Cognito token silently and return new idToken, or null on failure
export const silentTokenRefresh = async () => {
	const refreshToken = localStorage.getItem("refreshToken");
	if (!refreshToken) return null;
	try {
		const response = await axios.post(
			`https://${authConfig.cognitoDomain}/oauth2/token`,
			new URLSearchParams({
				grant_type: "refresh_token",
				client_id: authConfig.clientId,
				refresh_token: refreshToken,
			}),
			{ headers: { "Content-Type": "application/x-www-form-urlencoded" } },
		);
		const data = response.data;
		const expiryTime = Math.floor(Date.now() / 1000) + data.expires_in;
		localStorage.setItem("idToken", data.id_token);
		localStorage.setItem("accessToken", data.access_token);
		localStorage.setItem("expiryTime", expiryTime.toString());
		// refresh_token is not returned for refresh_token grants — keep existing one
		console.log("🔄 Token silently refreshed");

		// Clear user access cache so permissions are re-evaluated with new token
		try {
			const { default: userAccessService } = await import(
				"./userAccessService"
			);
			userAccessService.clearCache();
		} catch (_) {}

		return data.id_token;
	} catch (err) {
		console.warn("⚠️ Silent token refresh failed:", err);
		return null;
	}
};

// Helper function to get authentication headers
export const getAuthHeaders = () => {
	const idToken = localStorage.getItem("idToken");
	const username = localStorage.getItem("username");

	if (!idToken) {
		throw new Error("Authentication token missing");
	}

	const headers = {
		"Content-Type": "application/json",
		Authorization: `Bearer ${idToken}`,
	};

	// Add username to headers for authorization checks (backend expects X-User-Name)
	if (username) {
		headers["X-User-Name"] = username;
	}

	return headers;
};

// Create an axios instance with interceptor for authentication
const apiClient = axios.create({
	baseURL: API_BASE_URL,
});

// Add request interceptor to include auth headers from localStorage
apiClient.interceptors.request.use(
	(config) => {
		const idToken = localStorage.getItem("idToken");
		const username = localStorage.getItem("username");

		if (idToken) {
			config.headers.Authorization = `Bearer ${idToken}`;
		}

		// Add username to headers for authorization checks (backend expects X-User-Name)
		if (username) {
			config.headers["X-User-Name"] = username;
		}

		// Only set Content-Type to application/json if it's not FormData
		// For FormData, let the browser set the Content-Type with boundary
		if (!(config.data instanceof FormData)) {
			config.headers["Content-Type"] = "application/json";
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Track if a refresh is already in progress to avoid parallel refresh races
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
	refreshSubscribers.forEach((cb) => cb(newToken));
	refreshSubscribers = [];
};

const subscribeTokenRefresh = (cb) => {
	refreshSubscribers.push(cb);
};

// Add response interceptor — silently refresh token on 401 and retry the original request
apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			if (isRefreshing) {
				// Queue this request until the ongoing refresh completes
				return new Promise((resolve, reject) => {
					subscribeTokenRefresh((newToken) => {
						if (newToken) {
							originalRequest.headers.Authorization = `Bearer ${newToken}`;
							resolve(apiClient(originalRequest));
						} else {
							reject(error);
						}
					});
				});
			}

			isRefreshing = true;
			try {
				const newToken = await silentTokenRefresh();
				isRefreshing = false;

				if (newToken) {
					onRefreshed(newToken);
					originalRequest.headers.Authorization = `Bearer ${newToken}`;
					return apiClient(originalRequest);
				} else {
					// Refresh failed — redirect to login
					onRefreshed(null);
					localStorage.clear();
					window.location.href = "/";
				}
			} catch (refreshError) {
				isRefreshing = false;
				onRefreshed(null);
				localStorage.clear();
				window.location.href = "/";
			}
		}

		return Promise.reject(error);
	},
);

// Export the authenticated axios instance for use in other files
export { apiClient };

export const fetchPTSachNrData = async () => {
	try {
		const response = await apiClient.get("/ptsachnr");
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response.data;
	} catch (error) {
		console.error("Error fetching PTSachNr data:", error);
		throw error;
	}
};

export const fetchBezugsartData = async () => {
	try {
		const response = await apiClient.get("/bezugsart");
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response.data;
	} catch (error) {
		console.error("Error fetching Bezugsart data:", error);
		throw error;
	}
};

export const fetchVFFSNRData = async () => {
	try {
		const response = await apiClient.get("/vffsnr");
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response.data;
	} catch (error) {
		console.error("Error fetching VFFSNR data:", error);
		throw error;
	}
};

export const fetchSerieTrackingData = async () => {
	try {
		const response = await apiClient.get("/api/series-tracking");
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response.data;
	} catch (error) {
		console.error("Error fetching Serie Tracking data:", error);
		throw error;
	}
};

export const fetchKASTData = async () => {
	try {
		const response = await apiClient.get("/kast");
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response.data;
	} catch (error) {
		console.error("Error fetching KAST data:", error);
		throw error;
	}
};

// Translate a batch of texts (e.g. German part descriptions) to a target
// language via AWS Translate on the backend. Returns a { original: translated }
// map. Results are cached server-side, so repeat calls are cheap.
export const translateTexts = async (
	texts,
	targetLanguage = "en",
	sourceLanguage = "de",
) => {
	try {
		const response = await apiClient.post("/api/translate", {
			texts,
			target_language: targetLanguage,
			source_language: sourceLanguage,
		});
		return response.data?.translations || {};
	} catch (error) {
		console.error("Error translating texts:", error);
		return {};
	}
};

// Update the vehicle-level "band" value (M-Band / K-Band / C-Band) shown in the
// VFF dashboard. Pass an empty string to clear the value.
export const updateVehicleBand = async (vehicleNo, band) => {
	const response = await apiClient.post("/api/update-vehicle-band", {
		vehicle_no: vehicleNo,
		band: band,
	});
	return response.data;
};

// Bulk-update the vehicle band for several vehicles at once.
// `updates` is an array of { vehicle_no, band } objects (band "" clears it).
export const updateVehicleBandBulk = async (updates) => {
	const response = await apiClient.post("/api/update-vehicle-band-bulk", {
		updates,
	});
	return response.data;
};
